import { defineComponent, type PropType } from 'vue'

import CAIcon from '@ds/components/shared/CAIcon'
import type {
	CAButtonSize,
	CAButtonStyle,
	CAButtonType,
} from '@ds/interfaces'

/**
 * The `CAButton` component is used to display a button with various styles and sizes.
 * @component CAButton
 *
 * @prop {boolean} [active=false] - Whether the button is active.
 * @prop {string} [buttonId=''] - The ID of the button.
 * @prop {CAButtonStyle} [buttonStyle='filled-dark'] - The style of the button.
 * @prop {boolean} [collapsed=false] - Whether the button is collapsed.
 * @prop {string} [dataTest=''] - The data-test attribute for the button.
 * @prop {boolean} [disabled=false] - Whether the button is disabled.
 * @prop {string} [icon=''] - The icon to be displayed in the button.
 * @prop {string} [iconRight=''] - The icon to be displayed on the right side of the button.
 * @prop {boolean} [loading=false] - Whether the button is loading.
 * @prop {boolean} [sidebarButton=false] - Whether the button is a sidebar button.
 * @prop {CAButtonSize} [size='medium'] - The size of the button
 * @prop {string} [text=''] - The text to be displayed in the button.
 * @prop {CAButtonType} [type='primary'] - The type of the button.
 *
 * @emit click - Emits when the button is clicked.
 * @emit keydown - Emits when a key is pressed while the button is focused.
 *
 * @slot default - Default slot for button text or content.
 * @slot leftIcon - Slot for a custom left icon.
 * @slot rightIcon - Slot for a custom right icon.
 */
export default defineComponent({
	name: 'CAButton',

	props: {
		/**
		 * Whether the button is active.
		 * @default false
		 * @type {boolean}
		 */
		active: { default: false, type: Boolean },
		/**
		 * The ID of the button.
		 * @default ''
		 * @type {string}
		 */
		buttonId: { default: '', type: String },
		/**
		 * The style of the button.
		 * @default 'filled-dark'
		 * @type {CAButtonStyle}
		 * @enum {('dark' | 'filled-dark' | 'filled-light' | 'outlined')}
		 */
		buttonStyle: { default: 'filled-dark', type: String as PropType<CAButtonStyle> },
		/**
		 * Whether the button is collapsed.
		 * @default false
		 * @type {boolean}
		 */
		collapsed: { default: false, type: Boolean },
		/**
		 * The data-test attribute for the button.
		 * @default ''
		 * @type {string}
		 */
		dataTest: { default: '', type: String },
		/**
		 * Whether the button is disabled.
		 * @default false
		 * @type {boolean}
		 */
		disabled: { default: false, type: Boolean },
		/**
		 * The icon to be displayed in the button.
		 * @default ''
		 * @type {string}
		 */
		icon: { default: '', type: String },
		/**
		 * The icon to be displayed on the right side of the button.
		 * @default ''
		 * @type {string}
		 */
		iconRight: { default: '', type: String },
		/**
		 * Whether the button is loading.
		 * @default false
		 * @type {boolean}
		 */
		loading: { default: false, type: Boolean },
		/**
		 * Whether the button is a sidebar button.
		 * @default false
		 * @type {boolean}
		 */
		sidebarButton: { default: false, type: Boolean },
		/**
		 * The size of the button.
		 * @default 'medium'
		 * @type {CAButtonSize}
		 * @enum {('compact' | 'full' | 'large' | 'medium' | 'small')}
		 */
		size: { default: 'medium', type: String as PropType<CAButtonSize> },
		/**
		 * The text to be displayed in the button.
		 * @default ''
		 * @type {string}
		 */
		text: { default: '', type: String },
		/**
		 * The type of the button.
		 * @default 'primary'
		 * @type {CAButtonType}
		 * @enum {('primary' | 'danger' | 'group' | 'info' | 'primary-alt' | 'secondary' | 'slideover' | 'success' | 'warning')}
		 */
		type: { default: 'primary', type: String as PropType<CAButtonType> },
	},

	emits: {
		click: (_event: KeyboardEvent | MouseEvent) => true,
		keydown: (_event: KeyboardEvent) => true,
	},

	computed: {
		hasLeftIconSlot(): boolean {
			return !!this.$slots.leftIcon?.()
		},

		hasRightIconSlot(): boolean {
			return !!this.$slots.rightIcon?.()
		},
	},

	methods: {
		handleClick(action: KeyboardEvent | MouseEvent) {
			this.$emit('click', action)
		},

		handleKeyDown(keyClick: KeyboardEvent) {
			if (keyClick.key === 'Enter' || keyClick.key === ' ') {
				this.$emit('keydown', keyClick)
			}
		},
	},

	render() {
		return (
			<button
				class={[
					'care-academy-button',
					`care-academy-button__${this.size}`,
					`care-academy-button__${this.type}`,
					`care-academy-button__${this.buttonStyle}`,
					{
						'care-academy-button__dark': this.buttonStyle === 'dark' && !this.sidebarButton,
						'care-academy-button__disabled': this.disabled,
						'care-academy-button__filled-dark': this.buttonStyle === 'filled-dark' && !this.sidebarButton,
						'care-academy-button__filled-light': this.buttonStyle === 'filled-light' && !this.sidebarButton,
						'care-academy-button__full-width': this.size === 'full',
						'care-academy-button__group-button': this.type === 'group',
						'care-academy-button__group-button--active': this.type === 'group' && this.active,
						'care-academy-button__loading': this.loading,
						'care-academy-button__sidebar': this.sidebarButton,
						'care-academy-button__slideover': this.type === 'slideover',
						'care-academy-button__with-icon': this.icon !== '' || !!this.iconRight || this.hasLeftIconSlot,
						'care-academy-button__with-right-icon': !!this.iconRight || this.hasRightIconSlot,
					},
				]}
				data-test={this.dataTest}
				data-test-component="ca-button"
				disabled={this.disabled || this.loading}
				id={this.buttonId}
				tabindex={0}
				type="button"
				onClick={this.handleClick}
				onKeydown={this.handleKeyDown}
			>
				{this.loading && <CAIcon icon="SVGLoading" />}

				{!this.loading && this.icon === '' && this.type !== 'slideover' && this.$slots.leftIcon?.()}

				{!this.loading && this.icon !== '' && <CAIcon icon={this.icon} />}

				{!this.collapsed &&
					<span class="care-academy-button__contents">
						{this.$slots.default?.()}
						<span class="sr-only">{this.text}</span>
						{this.text}
					</span>
				}

				{this.type === 'slideover' && <CAIcon class="svg-selector" icon="SVGSelector" />}

				{!this.loading && this.$slots.rightIcon?.()}

				{!this.loading && this.iconRight !== '' && <CAIcon icon={this.iconRight} />}
			</button>
		)
	},
})
